import React, { FC, Suspense, useCallback, useEffect, useState } from 'react';
import './App.css';
import '@lib/css/its-vars.css';
import { APP_CFG } from './appcfg';
import {
  mobileDeviceDetected} from './redux/actions/ui';
import { isMobileDevice } from './redux/selectors/ui';
import ProgressBar from './components/progress-bar';
import NetworkError from './components/network-error';
import Snackbar from '@mui/material/Snackbar';
import Alert, { AlertColor } from '@mui/material/Alert';
import { ChoosePage } from './components/choose-page';
import { PageContent } from './page-content/page-content';
import { PageType } from 'its-pages';
import { LazyPageChooser } from './components/lazy';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import { getCurrentPageContent, getCurrentProblemProgress } from './redux/selectors/progression';
import { useSnapshot } from 'valtio';
import { Panel, sideBarState } from './sidebar-state';
import { ExpansionStyleType } from './components/expansion';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Container from '@mui/material/Container';
import { DuplicateInstanceDialog } from './components/duplicate-instance-dialog';

const LoadingStyle = styled.div`
  comment: LoadingStyle;
  display: inline-block;
  font-family: monospace;
  clip-path: inset(0 3ch 0 0);
  animation: l 1s steps(4) infinite;
`;

const Loading = (props: { error?: boolean }) => {
  if (props.error) {
    return <div>Error!</div>;
  } else {
    return <LoadingStyle>Loading...</LoadingStyle>;
  }
};

const MessageType: { [k in ExpansionStyleType]: AlertColor } = {
  "Correct": "success",
  "Incorrect": "error",
  "default": "info",
};

/* .ProgressBar { */
/* box-shadow: 4px 4px 4px var(--Box-Shadow-Color); */
/* } */

const ProblemBodyContainer = styled.div`
  comment: Body;
  grid-area: body;
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-width: 65em;
`;

const ProblemContentContainer = styled.div`
  overflow: auto;
`;

function ProblemBody(props: { progress: number, children: JSX.Element }) {
  const { openPanel, expansionInfos } = useSnapshot(sideBarState);
  const feedbackInfo = expansionInfos[Panel.Feedback];
  const hasFeedback = feedbackInfo && (openPanel === Panel.Feedback || openPanel === Panel.All);
  const isMobile = useAppSelector(isMobileDevice);

  const [isShowMessage, setShowMessage] = useState(true);
  const handleClose = useCallback(() => setShowMessage(false), []);
  useEffect(() => setShowMessage(true), [feedbackInfo]);
  const onCloseFeedback = useCallback(() => {
    setShowMessage(false);
  }, [setShowMessage]);
  const theme = useTheme();

  return <>
    <ProblemBodyContainer key={2}>
      <ProgressBar value={props.progress} />
      <ProblemContentContainer>
        {props.children}
      </ProblemContentContainer>
    </ProblemBodyContainer>
    <Snackbar
      onClick={onCloseFeedback}
      open={isShowMessage && hasFeedback}
      autoHideDuration={600000}
      onClose={handleClose}
      sx={{
        maxWidth: isMobile ? '100%' : '90%',
        // On small screen the tooltips cover the text in the snackbar, need to adjust the default z-index
        // https://mui.com/material-ui/customization/z-index/
        zIndex: isMobile ? theme.zIndex.tooltip + 1 : theme.zIndex.snackbar 
      }}
    >
      <Alert
        onClose={handleClose}
        severity={MessageType[expansionInfos.feedback?.style ?? "default"]}
      >
        {feedbackInfo?.elementRef ?? <></>}
      </Alert>
    </Snackbar>
  </>
};

const Page = styled.div`
  display: grid;
  grid-template-areas:
    'body body'
    'body body';
  margin-left: 0em;
  padding-top: 1em;
  grid-template-columns: auto 1fr;
  grid-template-rows: 2rem auto;
  font-family: Open Sans, Helvetica Neue, Helvetica, sans-serif;
  max-width: 80em;
  container: auto;
`;

const PageContainer = styled.div`
  display: flex;
  flexDirection: column;
  alignItems: center;
`;

const ProblemPage = (props: { problemContent: PageContent }) => {
  const progress = useAppSelector(getCurrentProblemProgress);

  /** @todo WHY?? Redux store data should be read-only. Always! */
  props.problemContent.pageData.progress = progress;

  return (
    <Page>
      <ProblemBody progress={progress}>
        {props.problemContent.component}
      </ProblemBody>
    </Page>
  );
};

export const App: FC = () => {

  const isLoading = useAppSelector(state => state.ui.isLoading);
  const networkError = useAppSelector(state => state.currentProblemState.networkError);
  const networkRetry = useAppSelector(state => state.currentProblemState.networkRetry);
  const progressionComplete = useAppSelector(state => state.progression.completed);
  const setup = useAppSelector(state => state.currentProblemState.setup);

  const isMobile = useAppSelector(isMobileDevice);
  const isNarrowScreen = useMediaQuery('(max-width: 1224px)');
  const isPortraitOrientation = useMediaQuery('(orientation: portrait)');
  const mobileDetected = isNarrowScreen && isPortraitOrientation;
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isMobile !== mobileDetected) {
      dispatch(mobileDeviceDetected(mobileDetected));
    }
  }, [isMobile, mobileDetected, dispatch]);

  const status = 
    progressionComplete
      ? 'All done!'
      : isLoading
      ? 'Loading...'
      : 'Initializing...';

  const problemContent = useAppSelector(getCurrentPageContent);
  const duplicateDetected = useAppSelector(
    (state) => state.ui.isDuplicateAppInstanceDetected
  );

  if (!problemContent || progressionComplete) {
    return (
      <div>
        {APP_CFG.testApp && progressionComplete ? (
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          <ChoosePage setup={setup!} />
        ) : null}
        <h2>{status}</h2>
      </div>
    );
  }

  let page: JSX.Element = <></>;

  switch (problemContent.pageType) {
    case PageType.Problem:
      page = <ProblemPage problemContent={problemContent} />
      break;
    case PageType.Banner:
      page = problemContent.component;
      break;
  }

  return (
    <Container sx={{ fontSize: '.9rem', p: 2 }}>
      {networkRetry && <NetworkError error={networkError} />}
      {APP_CFG.testApp && !isMobile && (
        <Suspense fallback={<Loading />}>
          <LazyPageChooser setup={setup!} />
        </Suspense>
      )}
      <div className='OverlayWrapper'>
        {networkRetry && <div className='Overlay' />}
        <PageContainer>
          {page}
        </PageContainer>
      </div>
      {duplicateDetected && <DuplicateInstanceDialog />}
    </Container>
  );
};

export default App;
